/**
 * ORVAN AI Copilot — floating chat drawer (Claude Sonnet 4.6, streaming SSE).
 * Modes: copilot (real business data) · rnd (Blend Builder) · content (ORVAN voice).
 * Other pages can open it with context:
 *   window.dispatchEvent(new CustomEvent("orvan-ai-open", { detail: { mode, context } }))
 */
import { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, Plus } from "lucide-react";

declare const process: { env: Record<string, string | undefined> };
const API = `${process.env.REACT_APP_BACKEND_URL}/api/pureh`;

type Msg = { role: "user" | "assistant"; content: string };
type Mode = "copilot" | "rnd" | "content";
const MODE_LABELS: { key: Mode; label: string }[] = [
  { key: "copilot", label: "مساعد ORVAN" },
  { key: "rnd", label: "مساعد R&D" },
  { key: "content", label: "محتوى" },
];
const PLACEHOLDERS: Record<Mode, string> = {
  copilot: "اسأل عن منتج، تكلفة، هامش، مخزون… مثال: هامش ORVYN في السعودية؟",
  rnd: "اسأل عن التركيبة أو اطلب تحسين الثبات/الإسقاط…",
  content: "مثال: اكتب وصف قصير عربي/إنجليزي لمنتج BASALT",
};

export const AICopilot = () => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("copilot");
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [pageCtx, setPageCtx] = useState<unknown>(null);
  const sessionsRef = useRef<Record<string, string>>({});
  const bodyRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const h = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      if (d.mode) switchMode(d.mode as Mode);
      if (d.context !== undefined) setPageCtx(d.context);
      setOpen(true);
    };
    window.addEventListener("orvan-ai-open", h);
    return () => window.removeEventListener("orvan-ai-open", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight });
  }, [msgs, open]);

  async function loadSession(m: Mode) {
    const sid = sessionsRef.current[m];
    if (!sid) { setMsgs([]); return; }
    try {
      const r = await fetch(`${API}/ai/sessions/${sid}`, { credentials: "include" });
      if (r.ok) {
        const j = await r.json();
        setMsgs((j.messages || []).map((x: Msg) => ({ role: x.role, content: x.content })));
      } else setMsgs([]);
    } catch { setMsgs([]); }
  }

  function switchMode(m: Mode) {
    abortRef.current?.abort();
    setBusy(false);
    setMode(m);
    setPageCtx(null);
    loadSession(m);
  }

  function newChat() {
    abortRef.current?.abort();
    setBusy(false);
    delete sessionsRef.current[mode];
    setMsgs([]);
    setPageCtx(null);
  }

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    const ac = new AbortController();
    abortRef.current = ac;
    const appendDelta = (t: string) => setMsgs((c) => {
      if (!c.length || c[c.length - 1].role !== "assistant") return c;
      const n = [...c];
      n[n.length - 1] = { role: "assistant", content: n[n.length - 1].content + t };
      return n;
    });
    const setLast = (content: string) => setMsgs((c) => {
      if (!c.length || c[c.length - 1].role !== "assistant") return c;
      const n = [...c];
      n[n.length - 1] = { role: "assistant", content };
      return n;
    });
    setMsgs((c) => [...c, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const r = await fetch(`${API}/ai/chat`, {
        method: "POST",
        credentials: "include",
        signal: ac.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode, message: text,
          sessionId: sessionsRef.current[mode] || undefined,
          context: pageCtx ?? undefined,
        }),
      });
      if (!r.ok || !r.body) throw new Error(`HTTP ${r.status}`);
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.sessionId) sessionsRef.current[mode] = ev.sessionId;
          if (ev.t) appendDelta(ev.t);
          if (ev.error) setLast(`تعذّر الرد: ${ev.error}`);
        }
      }
    } catch (e) {
      if ((e as Error).name !== "AbortError") {
        setLast(`خطأ في الاتصال: ${(e as Error).message}`);
      }
    } finally {
      setBusy(false);
    }
  }

  const GOLD = "var(--ph-gold, #B8965A)";
  return (
    <>
      <button
        data-testid="ai-copilot-fab"
        onClick={() => { setOpen((o) => !o); if (!open) loadSession(mode); }}
        aria-label="ORVAN AI"
        style={{
          position: "fixed", bottom: 22, right: 22, zIndex: 60,
          width: 52, height: 52, borderRadius: "50%",
          background: "#122240", color: GOLD, border: `1.5px solid ${GOLD}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 8px 24px rgba(18,34,64,0.35)", cursor: "pointer",
        }}
      >
        <Sparkles size={22} />
      </button>

      {open && (
        <div
          data-testid="ai-copilot-panel"
          dir="rtl"
          style={{
            position: "fixed", bottom: 84, right: 22, zIndex: 60,
            width: "min(420px, calc(100vw - 32px))", height: "min(600px, calc(100vh - 120px))",
            background: "#0F1D38", color: "#EDE6D6",
            border: `1px solid ${GOLD}`, borderRadius: 14,
            display: "flex", flexDirection: "column", overflow: "hidden",
            boxShadow: "0 16px 48px rgba(0,0,0,0.45)",
            fontFamily: "'Tajawal', sans-serif",
          }}
        >
          <div style={{ padding: "10px 14px", borderBottom: "1px solid rgba(184,150,90,0.35)", display: "flex", alignItems: "center", gap: 8 }}>
            <Sparkles size={16} style={{ color: GOLD }} />
            <span style={{ fontWeight: 700, letterSpacing: 1 }}>ORVAN AI</span>
            <span style={{ fontSize: 10, opacity: 0.6 }}>Claude Sonnet 4.6</span>
            <div style={{ marginRight: "auto", display: "flex", gap: 6 }}>
              <button data-testid="ai-new-chat-btn" onClick={newChat} title="محادثة جديدة"
                style={{ background: "none", border: "none", color: GOLD, cursor: "pointer" }}><Plus size={16} /></button>
              <button data-testid="ai-close-btn" onClick={() => setOpen(false)}
                style={{ background: "none", border: "none", color: "#EDE6D6", cursor: "pointer" }}><X size={16} /></button>
            </div>
          </div>

          <div style={{ display: "flex", gap: 6, padding: "8px 12px" }}>
            {MODE_LABELS.map((m) => (
              <button
                key={m.key}
                data-testid={`ai-mode-${m.key}`}
                onClick={() => switchMode(m.key)}
                style={{
                  padding: "4px 12px", borderRadius: 999, fontSize: 12, cursor: "pointer",
                  border: `1px solid ${GOLD}`,
                  background: mode === m.key ? GOLD : "transparent",
                  color: mode === m.key ? "#0F1D38" : "#EDE6D6",
                  fontWeight: mode === m.key ? 700 : 400,
                }}
              >{m.label}</button>
            ))}
          </div>

          {pageCtx != null && (
            <div data-testid="ai-context-chip" style={{ margin: "0 12px 6px", padding: "4px 10px", fontSize: 11, borderRadius: 8, background: "rgba(184,150,90,0.15)", border: "1px dashed rgba(184,150,90,0.5)" }}>
              📎 سياق الصفحة مرفق مع سؤالك
              <button onClick={() => setPageCtx(null)} style={{ background: "none", border: "none", color: GOLD, cursor: "pointer", marginRight: 6 }}>إزالة</button>
            </div>
          )}

          <div ref={bodyRef} data-testid="ai-messages" style={{ flex: 1, overflowY: "auto", padding: "8px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
            {msgs.length === 0 && (
              <div data-testid="ai-empty-hint" style={{ margin: "auto", textAlign: "center", opacity: 0.55, fontSize: 13, lineHeight: 2 }}>
                {PLACEHOLDERS[mode]}
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} data-testid={`ai-msg-${m.role}`} style={{
                alignSelf: m.role === "user" ? "flex-start" : "flex-end",
                maxWidth: "88%", padding: "8px 12px", borderRadius: 12, fontSize: 13.5, lineHeight: 1.8,
                whiteSpace: "pre-wrap",
                background: m.role === "user" ? "rgba(184,150,90,0.18)" : "rgba(255,255,255,0.06)",
                border: m.role === "user" ? "1px solid rgba(184,150,90,0.4)" : "1px solid rgba(255,255,255,0.08)",
              }}>
                {m.content || (busy && i === msgs.length - 1 ? "…" : "")}
              </div>
            ))}
          </div>

          <div style={{ padding: 10, borderTop: "1px solid rgba(184,150,90,0.35)", display: "flex", gap: 8 }}>
            <input
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={PLACEHOLDERS[mode]}
              disabled={busy}
              style={{ flex: 1, padding: "9px 12px", borderRadius: 10, border: "1px solid rgba(184,150,90,0.4)", background: "rgba(255,255,255,0.05)", color: "#EDE6D6", fontSize: 13, outline: "none", fontFamily: "inherit" }}
            />
            <button
              data-testid="ai-send-btn"
              onClick={send}
              disabled={busy || !input.trim()}
              style={{ width: 40, borderRadius: 10, border: "none", background: GOLD, color: "#0F1D38", cursor: busy ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: busy || !input.trim() ? 0.5 : 1 }}
            ><Send size={16} style={{ transform: "scaleX(-1)" }} /></button>
          </div>
        </div>
      )}
    </>
  );
};
